import { intersection, map, property } from 'lodash';

import { FEATURE_FLAG } from 'components/ContentReporting/feature-flags';
import { isMusicVideosReportingEnabledForCountry } from 'components/ContentReporting/market-helper';
import { AssociatedContent, EntityType } from 'proto/generated/inspector_gadget';
import { AssetType } from 'proto/generated/spotify/catalogmanagement/entity/v1/asset';

import { ASSOCIATED_CONTENT_TYPES, CLAIM_TYPE, CONTENT_TYPE, ENTITY_TYPES_WITH_ASSOCIATED_CONTENT } from '../types';

export const getContentTypeOptions = (
  claimType: CLAIM_TYPE,
  userCountryIsoCode: string,
  isFeatureFlagEnabled?: (featureFlag: FEATURE_FLAG) => boolean,
) => {
  switch (claimType) {
    case CLAIM_TYPE.TRADEMARK:
    case CLAIM_TYPE.COPYRIGHT:
    case CLAIM_TYPE.OTHER:
      return [
        CONTENT_TYPE.MUSIC,
        CONTENT_TYPE.PODCAST,
        CONTENT_TYPE.AUDIOBOOK,
        CONTENT_TYPE.ARTWORK,
        CONTENT_TYPE.USER_IMAGE,
        CONTENT_TYPE.PLAYLIST_IMAGE,
        CONTENT_TYPE.PLAYLIST_TITLE,
        CONTENT_TYPE.PLAYLIST_DESCRIPTION,
        CONTENT_TYPE.ARTIST_PROFILE_IMAGE,
        CONTENT_TYPE.ARTIST_BANNER_IMAGE,
        CONTENT_TYPE.ARTIST_GALLERY_IMAGE,
        CONTENT_TYPE.ARTIST_BIO_TEXT,
        CONTENT_TYPE.ARTIST_PICK_TEXT,
        CONTENT_TYPE.ARTIST_MERCHANDISE,
        CONTENT_TYPE.NOW_PLAYING,
        CONTENT_TYPE.ARTIST_VIDEO_VIDEO,
        CONTENT_TYPE.ARTIST_VIDEO_TITLE,
        CONTENT_TYPE.ARTIST_VIDEO_AUDIO,
        CONTENT_TYPE.POLL_QUESTION_TEXT,
        CONTENT_TYPE.COMMENT_TEXT,
        CONTENT_TYPE.POLL_OPTIONS_TEXT,
        CONTENT_TYPE.MERCH_TITLE,
        CONTENT_TYPE.MERCH_DESCRIPTION,
        CONTENT_TYPE.MERCH_IMAGE_GALLERY,
        ...(isMusicVideosReportingEnabledForCountry(userCountryIsoCode) ? [CONTENT_TYPE.MUSIC_VIDEO] : []),
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_TITLE,
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_AUDIO,
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_VIDEO,
        CONTENT_TYPE.SUPPLEMENTARY_PDF,
        ...(isFeatureFlagEnabled?.(FEATURE_FLAG.CHAT_MESSAGE) ? [CONTENT_TYPE.CHAT_MESSAGE_TEXT] : []),
      ];
    case CLAIM_TYPE.PUBLICITY_LIKENESS:
      return [
        CONTENT_TYPE.MUSIC,
        CONTENT_TYPE.ARTWORK,
        CONTENT_TYPE.USER_IMAGE,
        CONTENT_TYPE.PLAYLIST_IMAGE,
        CONTENT_TYPE.PLAYLIST_TITLE,
        CONTENT_TYPE.PLAYLIST_DESCRIPTION,
        CONTENT_TYPE.ARTIST_PROFILE_IMAGE,
        CONTENT_TYPE.ARTIST_BANNER_IMAGE,
        CONTENT_TYPE.ARTIST_GALLERY_IMAGE,
        CONTENT_TYPE.ARTIST_MERCHANDISE,
        CONTENT_TYPE.NOW_PLAYING,
        CONTENT_TYPE.ARTIST_VIDEO_VIDEO,
        CONTENT_TYPE.ARTIST_VIDEO_TITLE,
        CONTENT_TYPE.ARTIST_VIDEO_AUDIO,
        CONTENT_TYPE.POLL_QUESTION_TEXT,
        CONTENT_TYPE.COMMENT_TEXT,
        CONTENT_TYPE.POLL_OPTIONS_TEXT,
        CONTENT_TYPE.MERCH_TITLE,
        CONTENT_TYPE.MERCH_DESCRIPTION,
        CONTENT_TYPE.MERCH_IMAGE_GALLERY,
        ...(isMusicVideosReportingEnabledForCountry(userCountryIsoCode) ? [CONTENT_TYPE.MUSIC_VIDEO] : []),
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_TITLE,
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_AUDIO,
        CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_VIDEO,
        CONTENT_TYPE.SUPPLEMENTARY_PDF,
        ...(isFeatureFlagEnabled?.(FEATURE_FLAG.CHAT_MESSAGE) ? [CONTENT_TYPE.CHAT_MESSAGE_TEXT] : []),
      ];
    default:
      return [];
  }
};

export const getAssetTypeOptions = (
  claimType: CLAIM_TYPE,
  userCountryIsoCode: string,
  isFeatureFlagEnabled?: (featureFlag: FEATURE_FLAG) => boolean,
) => {
  switch (claimType) {
    case CLAIM_TYPE.TRADEMARK:
    case CLAIM_TYPE.COPYRIGHT:
    case CLAIM_TYPE.OTHER:
      return [
        { contentType: CONTENT_TYPE.MUSIC, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.PODCAST, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.AUDIOBOOK, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.ARTWORK, commonAssetType: AssetType.IMAGE },
        { contentType: CONTENT_TYPE.USER_IMAGE, commonAssetType: AssetType.PROFILE_IMAGE },
        { contentType: CONTENT_TYPE.PLAYLIST_IMAGE, commonAssetType: AssetType.PLAYLIST_IMAGE },
        { contentType: CONTENT_TYPE.PLAYLIST_TITLE, commonAssetType: AssetType.TITLE },
        { contentType: CONTENT_TYPE.PLAYLIST_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
        { contentType: CONTENT_TYPE.ARTIST_PROFILE_IMAGE, commonAssetType: AssetType.ARTIST_AVATAR },
        { contentType: CONTENT_TYPE.ARTIST_BANNER_IMAGE, commonAssetType: AssetType.ARTIST_HEADER },
        { contentType: CONTENT_TYPE.ARTIST_GALLERY_IMAGE, commonAssetType: AssetType.ARTIST_GALLERY_IMAGE },
        { contentType: CONTENT_TYPE.ARTIST_BIO_TEXT, commonAssetType: AssetType.ARTIST_BIOGRAPHY },
        { contentType: CONTENT_TYPE.ARTIST_PICK_TEXT, commonAssetType: AssetType.ARTIST_PICK },
        { contentType: CONTENT_TYPE.ARTIST_MERCHANDISE, commonAssetType: AssetType.ARTIST_MERCHANDISE },
        { contentType: CONTENT_TYPE.NOW_PLAYING, commonAssetType: AssetType.CANVAS_ASSET },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_VIDEO, commonAssetType: AssetType.VIDEO },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_TITLE, commonAssetType: AssetType.TITLE },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_AUDIO, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.POLL_QUESTION_TEXT, commonAssetType: AssetType.POLL_QUESTION_TEXT },
        { contentType: CONTENT_TYPE.COMMENT_TEXT, commonAssetType: AssetType.COMMENT_TEXT },
        { contentType: CONTENT_TYPE.POLL_OPTIONS_TEXT, commonAssetType: AssetType.POLL_OPTIONS_TEXT },
        { contentType: CONTENT_TYPE.MERCH_TITLE, commonAssetType: AssetType.MERCH_TITLE },
        { contentType: CONTENT_TYPE.MERCH_DESCRIPTION, commonAssetType: AssetType.MERCH_DESCRIPTION },
        { contentType: CONTENT_TYPE.MERCH_IMAGE_GALLERY, commonAssetType: AssetType.MERCH_IMAGE_GALLERY },
        ...(isMusicVideosReportingEnabledForCountry(userCountryIsoCode)
          ? [{ contentType: CONTENT_TYPE.MUSIC_VIDEO, commonAssetType: AssetType.VIDEO }]
          : []),
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_TITLE,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_TITLE,
        },
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_AUDIO,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_AUDIO,
        },
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_VIDEO,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_VIDEO,
        },
        { contentType: CONTENT_TYPE.SUPPLEMENTARY_PDF, commonAssetType: AssetType.SUPPLEMENTARY_PDF },
        ...(isFeatureFlagEnabled?.(FEATURE_FLAG.CHAT_MESSAGE)
          ? [{ contentType: CONTENT_TYPE.CHAT_MESSAGE_TEXT, commonAssetType: AssetType.CHAT_MESSAGE_TEXT }]
          : []),
      ];
    case CLAIM_TYPE.PUBLICITY_LIKENESS:
      return [
        { contentType: CONTENT_TYPE.MUSIC, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.ARTWORK, commonAssetType: AssetType.IMAGE },
        { contentType: CONTENT_TYPE.USER_IMAGE, commonAssetType: AssetType.PROFILE_IMAGE },
        { contentType: CONTENT_TYPE.PLAYLIST_IMAGE, commonAssetType: AssetType.PLAYLIST_IMAGE },
        { contentType: CONTENT_TYPE.PLAYLIST_TITLE, commonAssetType: AssetType.TITLE },
        { contentType: CONTENT_TYPE.PLAYLIST_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
        { contentType: CONTENT_TYPE.ARTIST_PROFILE_IMAGE, commonAssetType: AssetType.ARTIST_AVATAR },
        { contentType: CONTENT_TYPE.ARTIST_BANNER_IMAGE, commonAssetType: AssetType.ARTIST_HEADER },
        { contentType: CONTENT_TYPE.ARTIST_GALLERY_IMAGE, commonAssetType: AssetType.ARTIST_GALLERY_IMAGE },
        { contentType: CONTENT_TYPE.ARTIST_MERCHANDISE, commonAssetType: AssetType.ARTIST_MERCHANDISE },
        { contentType: CONTENT_TYPE.NOW_PLAYING, commonAssetType: AssetType.CANVAS_ASSET },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_VIDEO, commonAssetType: AssetType.VIDEO },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_TITLE, commonAssetType: AssetType.TITLE },
        { contentType: CONTENT_TYPE.ARTIST_VIDEO_AUDIO, commonAssetType: AssetType.AUDIO },
        { contentType: CONTENT_TYPE.POLL_QUESTION_TEXT, commonAssetType: AssetType.POLL_QUESTION_TEXT },
        { contentType: CONTENT_TYPE.COMMENT_TEXT, commonAssetType: AssetType.COMMENT_TEXT },
        { contentType: CONTENT_TYPE.POLL_OPTIONS_TEXT, commonAssetType: AssetType.POLL_OPTIONS_TEXT },
        { contentType: CONTENT_TYPE.MERCH_TITLE, commonAssetType: AssetType.MERCH_TITLE },
        { contentType: CONTENT_TYPE.MERCH_DESCRIPTION, commonAssetType: AssetType.MERCH_DESCRIPTION },
        { contentType: CONTENT_TYPE.MERCH_IMAGE_GALLERY, commonAssetType: AssetType.MERCH_IMAGE_GALLERY },
        ...(isMusicVideosReportingEnabledForCountry(userCountryIsoCode)
          ? [{ contentType: CONTENT_TYPE.MUSIC_VIDEO, commonAssetType: AssetType.VIDEO }]
          : []),
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_TITLE,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_TITLE,
        },
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_AUDIO,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_AUDIO,
        },
        {
          contentType: CONTENT_TYPE.EPISODE_VIDEO_PREVIEW_VIDEO,
          commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_VIDEO,
        },
        { contentType: CONTENT_TYPE.SUPPLEMENTARY_PDF, commonAssetType: AssetType.SUPPLEMENTARY_PDF },
        ...(isFeatureFlagEnabled?.(FEATURE_FLAG.CHAT_MESSAGE)
          ? [{ contentType: CONTENT_TYPE.CHAT_MESSAGE_TEXT, commonAssetType: AssetType.CHAT_MESSAGE_TEXT }]
          : []),
      ];
    default:
      return [];
  }
};

export const couldHaveAssociatedContent = (contentType: CONTENT_TYPE, entityType: EntityType | EntityType[]) => {
  const isValidEntityTypes = Array.isArray(entityType)
    ? Boolean(intersection([...ENTITY_TYPES_WITH_ASSOCIATED_CONTENT], entityType).length)
    : ENTITY_TYPES_WITH_ASSOCIATED_CONTENT.has(entityType);

  return ASSOCIATED_CONTENT_TYPES.has(contentType) && isValidEntityTypes;
};

export const getUrisWithoutASelectedAssociatedContent = (
  reportedAssetId: AssociatedContent[],
  reportedUris: string[],
  associatedContent: any[],
): string[] => {
  if (!reportedAssetId.length) {
    return map(associatedContent, property('parentUri'));
  }

  return associatedContent
    .filter(item => !item.elements.find((element: any) => reportedAssetId.find(asset => asset.value === element.value)))
    .map(item => item.parentUri)
    .filter(assetParentUri => reportedUris.includes(assetParentUri));
};
